import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import { IndicativeBadge } from "@/components/IndicativeBadge";
import { AHDBOfficialPrices } from "@/components/AHDBOfficialPrices";
import { SharePricesButton } from "@/components/SharePricesButton";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import GlobalPriceHistoryModal from "@/components/GlobalPriceHistoryModal";
import GlobalAlertModal from "@/components/GlobalAlertModal";
import {
  CaretUp, CaretDown, Cow, Storefront, Factory, Minus, MagnifyingGlass,
  ArrowsDownUp, CircleNotch, Trophy, Medal, FlagBanner, ChartLine, Bell, PushPin, Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const GBP_TO_USD = 1.27; // 1 GBP = 1.27 USD

export default function UKMarketsPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("factories");
  
  const [factories, setFactories] = useState([]);
  const [factoriesLoading, setFactoriesLoading] = useState(true);
  const [factoryRegion, setFactoryRegion] = useState("");
  const [factorySortBy, setFactorySortBy] = useState("price_desc");
  
  const [marts, setMarts] = useState([]);
  const [martStats, setMartStats] = useState(null);
  const [martsLoading, setMartsLoading] = useState(true);
  const [martRegion, setMartRegion] = useState("");
  const [martCounty, setMartCounty] = useState("");
  const [martCategory, setMartCategory] = useState("steers_400_500kg");
  const [martSortBy, setMartSortBy] = useState("price_desc");
  const [searchQuery, setSearchQuery] = useState("");
  
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  
  const [historyModal, setHistoryModal] = useState({ open: false, market: null, category: null, price: null });
  const [alertModal, setAlertModal] = useState({ open: false, market: null, price: null, category: null, marketType: null });

  const fetchRegions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/uk/regions`);
      setRegions(response.data.regions);
    } catch (err) {
      console.error("Failed to fetch regions:", err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/uk/categories`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  }, []);

  const fetchWatchlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/user/watchlist`, { headers: { Authorization: `Bearer ${token}` } });
      setWatchlist(response.data);
    } catch (err) {
      console.error("Failed to fetch watchlist:", err);
    }
  }, [token]);

  const fetchFactories = useCallback(async () => {
    setFactoriesLoading(true);
    try {
      const response = await axios.get(`${API}/uk/factories`, {
        params: { region: factoryRegion || undefined, sort_by: factorySortBy }
      });
      setFactories(response.data.factories);
      setLastUpdated(response.data.last_updated);
    } catch (error) { toast.error("Failed to load UK factory prices"); }
    finally { setFactoriesLoading(false); }
  }, [factoryRegion, factorySortBy]);

  const fetchMarts = useCallback(async () => {
    setMartsLoading(true);
    try {
      const response = await axios.get(`${API}/uk/marts`, {
        params: { region: martRegion || undefined, county: martCounty || undefined, category: martCategory, sort_by: martSortBy }
      });
      setMarts(response.data.marts);
      setMartStats(response.data.stats);
      setLastUpdated(response.data.last_updated);
    } catch (error) { toast.error("Failed to load UK mart prices"); }
    finally { setMartsLoading(false); }
  }, [martRegion, martCounty, martCategory, martSortBy]);

  useEffect(() => {
    fetchRegions();
    fetchCategories();
    if (token) fetchWatchlist();
  }, [token, fetchRegions, fetchCategories, fetchWatchlist]);

  useEffect(() => {
    if (activeTab === "factories") fetchFactories();
    else fetchMarts();
  }, [activeTab, fetchFactories, fetchMarts]);

  const toggleWatchlist = async (marketName, marketType, category = null) => {
    if (!token) { toast.error("Please sign in to use watchlist"); return; }
    const isIn = watchlist.some(w => w.market_name === marketName && w.country === "uk" && w.category === category);
    try {
      if (isIn) {
        const item = watchlist.find(w => w.market_name === marketName && w.country === "uk" && w.category === category);
        await axios.delete(`${API}/user/watchlist/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Removed from watchlist");
      } else {
        await axios.post(`${API}/user/watchlist`, { country: "uk", market_type: marketType, market_name: marketName, category }, 
          { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Added to watchlist");
      }
      fetchWatchlist();
    } catch (error) { toast.error("Failed to update watchlist"); }
  };

  const isInWatchlist = (marketName, category = null) => watchlist.some(w => w.market_name === marketName && w.country === "uk" && w.category === category);

  const formatPrice = (price, unit) => {
    if (!price) return "N/A";
    if (unit === "£/head") return `£${price.toFixed(0)}`;
    return `${price.toFixed(1)}p/kg`;
  };

  const formatUSD = (price, unit) => {
    if (!price) return "";
    const priceInPounds = unit === "£/head" ? price : price / 100;
    const usd = priceInPounds * GBP_TO_USD;
    if (unit === "£/head") return `≈ $${Math.round(usd).toLocaleString()}`;
    return `≈ $${(usd * 100).toFixed(0)}¢/kg`;
  };

  const getChangeColor = (change) => change > 0 ? "text-[#059669]" : change < 0 ? "text-[#DC2626]" : "text-[#6B6661]";
  const getChangeBg = (change) => change > 0 ? "bg-[#ECFDF5]" : change < 0 ? "bg-[#FEF2F2]" : "bg-[#F6E9DD]";

  const getCounties = () => {
    if (martRegion) {
      const region = regions.find(r => r.name === martRegion);
      return region ? region.counties : [];
    }
    return regions.flatMap(r => r.counties || []);
  };

  const filteredFactories = factories.filter(f => f.factory_name.toLowerCase().includes(searchQuery.toLowerCase()) || f.location.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredMarts = marts.filter(m => m.mart_name.toLowerCase().includes(searchQuery.toLowerCase()) || m.county.toLowerCase().includes(searchQuery.toLowerCase()));

  const currentCategory = categories.find(c => c.id === martCategory);

  const getRegionFlag = (region) => {
    switch (region) {
      case "England": return "🏴󠁧󠁢󠁥󠁮󠁧󠁿";
      case "Scotland": return "🏴󠁧󠁢󠁳󠁣󠁴󠁿";
      case "Wales": return "🏴󠁧󠁢󠁷󠁬󠁳󠁿";
      default: return "🇬🇧";
    }
  };

  const getRankBadge = (idx) => {
    if (idx === 0) return <Trophy size={18} className="text-[#FCD34D]" weight="fill" />;
    if (idx === 1) return <Medal size={18} className="text-[#8B8580]" weight="fill" />;
    if (idx === 2) return <Medal size={18} className="text-[#B45309]" weight="fill" />;
    return <span className="text-[#6B6661] font-mono text-sm">{idx + 1}</span>;
  };

  return (
    <div className="min-h-screen bg-[#022C22]">
      <SEO title="UK Livestock Market Prices" description="Live UK cattle and sheep prices from factories and livestock marts. Compare deadweight and liveweight prices across England, Scotland, Wales and NI." url="/uk-markets" />
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-2 py-1 bg-[#0F766E] text-white text-xs font-mono uppercase tracking-wider">UK Markets</span>
            <span className="px-2 py-1 bg-[#FEF3C7] text-[#92400E] text-xs font-mono uppercase tracking-wider">
              {activeTab === "factories" ? `${filteredFactories.length} Factories` : `${filteredMarts.length} Marts`}
            </span>
            <span className="px-2 py-1 bg-[#065F46] text-white text-xs font-mono flex items-center gap-1">
              <Clock size={12} /> 15min updates
            </span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-white mb-2">United Kingdom Livestock Markets</h1>
          <div className="mb-2"><IndicativeBadge /></div>
          <p className="text-[#8B8580] font-sans">Indicative prices for meat processors and livestock marts. <span className="text-[#6B6661]">Prices in GBP with USD equivalent.</span></p>
          <div className="mt-3"><SharePricesButton /></div>
        </div>

        <AHDBOfficialPrices />

        <div className="flex items-center gap-2 mb-6">
          <button onClick={() => setActiveTab("factories")} data-testid="tab-factories"
            className={`px-4 py-2 font-sans text-sm transition-colors ${activeTab === "factories" ? "bg-[#0F766E] text-white" : "bg-[#065F46] text-white/70 hover:text-white"}`}>
            <Factory size={16} className="inline mr-2" />Factory Prices
          </button>
          <button onClick={() => setActiveTab("marts")} data-testid="tab-marts"
            className={`px-4 py-2 font-sans text-sm transition-colors ${activeTab === "marts" ? "bg-[#0F766E] text-white" : "bg-[#065F46] text-white/70 hover:text-white"}`}>
            <Storefront size={16} className="inline mr-2" />Livestock Marts
          </button>
        </div>

        {activeTab === "factories" && (
          <>
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-[#064E3B] border border-[#065F46]">
              <select value={factoryRegion} onChange={(e) => setFactoryRegion(e.target.value)} data-testid="factory-region-filter"
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#0F766E]">
                <option value="">All Regions</option>
                <option value="England">England</option>
                <option value="Scotland">Scotland</option>
                <option value="Wales">Wales</option>
                <option value="Northern Ireland">Northern Ireland</option>
              </select>
              <select value={factorySortBy} onChange={(e) => setFactorySortBy(e.target.value)}
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm focus:outline-none focus:border-[#0F766E]">
                <option value="price_desc">Highest Price</option>
                <option value="price_asc">Lowest Price</option>
                <option value="name">Name (A-Z)</option>
              </select>
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input type="text" placeholder="Search factories..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-[#065F46] border border-[#0F766E] text-white placeholder-white/50 font-sans text-sm focus:outline-none focus:border-[#0F766E]" />
              </div>
            </div>

            <div className="bg-white border border-[#E0D4C8] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="factories-table">
                  <thead className="bg-[#F6E9DD]">
                    <tr>
                      <th className="text-center px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661] w-12">#</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Factory</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Location</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Region</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Price</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">USD</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">15min</th>
                      <th className="text-center px-2 py-3 text-xs font-mono uppercase tracking-wider text-[#6B6661]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {factoriesLoading ? (
                      <tr><td colSpan={8} className="px-4 py-12 text-center">
                        <CircleNotch size={32} className="animate-spin text-[#1D4ED8] mx-auto mb-2" />
                      </td></tr>
                    ) : filteredFactories.map((factory, idx) => (
                      <tr key={factory.id || idx} className="hover:bg-[#F9FAFB] transition-colors">
                        <td className="px-3 py-3 text-center">{getRankBadge(idx)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Factory size={16} className="text-[#1D4ED8]" />
                            <span className="text-[#33302E] text-sm">{factory.factory_name}</span>
                            {isInWatchlist(factory.factory_name) && <PushPin size={14} className="text-[#F59E0B]" weight="fill" />}
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-[#6B6661] text-sm">{factory.location}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 bg-[#E5E7EB] text-[#1D4ED8] text-xs font-mono">{getRegionFlag(factory.region)} {factory.region}</span></td>
                        <td className="px-4 py-3 text-right"><span className="font-mono text-sm text-[#33302E]">{formatPrice(factory.price, factory.unit)}</span></td>
                        <td className="px-4 py-3 text-right"><span className="font-mono text-sm text-[#6B6661]">{formatUSD(factory.price, factory.unit)}</span></td>
                        <td className="px-4 py-3 text-right">
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 ${getChangeBg(factory.change)}`}>
                            {factory.change > 0 ? <CaretUp size={12} className="text-[#059669]" weight="fill" /> : factory.change < 0 ? <CaretDown size={12} className="text-[#DC2626]" weight="fill" /> : <Minus size={12} />}
                            <span className={`font-mono text-sm ${getChangeColor(factory.change_percent)}`}>{factory.change_percent >= 0 ? "+" : ""}{factory.change_percent.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setHistoryModal({ open: true, market: factory.factory_name, category: null, price: factory.price })} className="p-1.5 hover:bg-[#E5E7EB]" title="View History"><ChartLine size={16} className="text-[#0F766E]" /></button>
                            <button onClick={() => setAlertModal({ open: true, market: factory.factory_name, price: factory.price, category: null, marketType: "factory" })} className="p-1.5 hover:bg-[#E5E7EB]" title="Set Alert"><Bell size={16} className="text-[#F59E0B]" /></button>
                            <button onClick={() => toggleWatchlist(factory.factory_name, "factory")} className="p-1.5 hover:bg-[#E5E7EB]" title="Watchlist"><PushPin size={16} className={isInWatchlist(factory.factory_name) ? "text-[#F59E0B]" : "text-[#6B6661]"} weight={isInWatchlist(factory.factory_name) ? "fill" : "regular"} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {activeTab === "marts" && (
          <>
            <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-[#064E3B] border border-[#065F46]">
              <select value={martCategory} onChange={(e) => setMartCategory(e.target.value)}
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm">
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
              <select value={martRegion} onChange={(e) => { setMartRegion(e.target.value); setMartCounty(""); }}
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm">
                <option value="">All Regions</option>
                {regions.map(r => <option key={r.name} value={r.name}>{r.name}</option>)}
              </select>
              <select value={martSortBy} onChange={(e) => setMartSortBy(e.target.value)}
                className="px-3 py-2 bg-[#065F46] border border-[#0F766E] text-white font-sans text-sm">
                <option value="price_desc">Highest Price</option>
                <option value="price_asc">Lowest Price</option>
                <option value="name">Name (A-Z)</option>
              </select>
              <div className="relative flex-1 min-w-[200px]">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                <input type="text" placeholder="Search marts..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 bg-[#065F46] border border-[#0F766E] text-white placeholder-white/50 font-sans text-sm" />
              </div>
            </div>

            <div className="bg-white border border-[#E0D4C8] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="marts-table">
                  <thead className="bg-[#F6E9DD]">
                    <tr>
                      <th className="text-center px-3 py-3 text-xs font-mono uppercase text-[#6B6661] w-12">#</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase text-[#6B6661]">Mart</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase text-[#6B6661]">County</th>
                      <th className="text-left px-4 py-3 text-xs font-mono uppercase text-[#6B6661]">Region</th>
                      <th className="text-center px-4 py-3 text-xs font-mono uppercase text-[#6B6661]">Day</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase text-[#6B6661]">Price</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase text-[#6B6661]">USD</th>
                      <th className="text-right px-4 py-3 text-xs font-mono uppercase text-[#6B6661]">15min</th>
                      <th className="text-center px-2 py-3 text-xs font-mono uppercase text-[#6B6661]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E7EB]">
                    {martsLoading ? (
                      <tr><td colSpan={9} className="px-4 py-12 text-center"><CircleNotch size={32} className="animate-spin text-[#1D4ED8] mx-auto" /></td></tr>
                    ) : filteredMarts.map((mart, idx) => (
                      <tr key={mart.id || idx} className="hover:bg-[#F9FAFB]">
                        <td className="px-3 py-3 text-center">{getRankBadge(idx)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Storefront size={16} className="text-[#1D4ED8]" />
                            <span className="text-[#33302E] text-sm">{mart.mart_name}</span>
                            {isInWatchlist(mart.mart_name, martCategory) && <PushPin size={14} className="text-[#F59E0B]" weight="fill" />}
                          </div>
                        </td>
                        <td className="px-4 py-3"><span className="text-[#6B6661] text-sm">{mart.county}</span></td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 bg-[#E5E7EB] text-[#1D4ED8] text-xs font-mono">{getRegionFlag(mart.region)} {mart.region}</span></td>
                        <td className="px-4 py-3 text-center"><span className="text-[#6B6661] text-xs">{mart.market_day}</span></td>
                        <td className="px-4 py-3 text-right"><span className="font-mono text-sm text-[#33302E]">{formatPrice(mart.price, mart.unit)}</span></td>
                        <td className="px-4 py-3 text-right"><span className="font-mono text-sm text-[#6B6661]">{formatUSD(mart.price, mart.unit)}</span></td>
                        <td className="px-4 py-3 text-right">
                          <div className={`inline-flex items-center gap-1 px-2 py-0.5 ${getChangeBg(mart.change)}`}>
                            {mart.change > 0 ? <CaretUp size={12} className="text-[#059669]" weight="fill" /> : mart.change < 0 ? <CaretDown size={12} className="text-[#DC2626]" weight="fill" /> : <Minus size={12} />}
                            <span className={`font-mono text-sm ${getChangeColor(mart.change_percent)}`}>{mart.change_percent >= 0 ? "+" : ""}{mart.change_percent.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="px-2 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => setHistoryModal({ open: true, market: mart.mart_name, category: martCategory, price: mart.price })} className="p-1.5 hover:bg-[#E5E7EB]"><ChartLine size={16} className="text-[#0F766E]" /></button>
                            <button onClick={() => setAlertModal({ open: true, market: mart.mart_name, price: mart.price, category: martCategory, marketType: "mart" })} className="p-1.5 hover:bg-[#E5E7EB]"><Bell size={16} className="text-[#F59E0B]" /></button>
                            <button onClick={() => toggleWatchlist(mart.mart_name, "mart", martCategory)} className="p-1.5 hover:bg-[#E5E7EB]"><PushPin size={16} className={isInWatchlist(mart.mart_name, martCategory) ? "text-[#F59E0B]" : "text-[#6B6661]"} weight={isInWatchlist(mart.mart_name, martCategory) ? "fill" : "regular"} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {martStats && (
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Highest</span>
                  <p className="font-mono text-xl text-[#059669] mt-1">{formatPrice(martStats.highest, currentCategory?.unit)}</p>
                  <p className="font-mono text-xs text-[#6B6661]">{formatUSD(martStats.highest, currentCategory?.unit)}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Average</span>
                  <p className="font-mono text-xl text-[#33302E] mt-1">{formatPrice(martStats.average, currentCategory?.unit)}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Lowest</span>
                  <p className="font-mono text-xl text-[#DC2626] mt-1">{formatPrice(martStats.lowest, currentCategory?.unit)}</p>
                </div>
                <div className="bg-white border border-[#E0D4C8] p-4">
                  <span className="text-xs font-mono text-[#6B6661] uppercase">Total</span>
                  <p className="font-mono text-xl text-[#33302E] mt-1">{martStats.total_marts}</p>
                </div>
              </div>
            )}
          </>
        )}

        {lastUpdated && (
          <div className="mt-6 text-center">
            <span className="text-xs text-[#6B6661]">Last updated: {new Date(lastUpdated).toLocaleString()} • Prices update every 15 minutes</span>
          </div>
        )}
      </main>
      <Footer />

      <GlobalPriceHistoryModal isOpen={historyModal.open} onClose={() => setHistoryModal({ open: false, market: null, category: null, price: null })}
        country="uk" marketName={historyModal.market} category={historyModal.category} currentPrice={historyModal.price} currency="GBP" unit="p/kg" />

      <GlobalAlertModal isOpen={alertModal.open} onClose={() => setAlertModal({ open: false, market: null, price: null, category: null })}
        country="uk" marketType={alertModal.marketType} marketName={alertModal.market} category={alertModal.category}
        currentPrice={alertModal.price} currency="GBP" unit="p/kg" token={token} />
    </div>
  );
}
